import React, { useEffect, useMemo, useState } from 'react';
import {
  LOST_REASONS,
  addCallNote,
  bookCall,
  dismissCall,
  INVOICE_LIVE,
  getCallItem,
  invoiceQuoteIdx,
  loadDetail,
  sendInvoice,
  noAnswerCall,
  scheduleCall,
  togglePriority,
  useCallDetail,
  useCalls,
  zohoQuoteTotal,
} from '../../services/callsBoard';
import { FOLLOW_UP_PRESETS, localDate } from '../../services/dealOutcome';
import { useSentInspections } from '../../services/drafts';
import { collectPhotos } from '../Routes/photos';
import { APP_CALLS_LIVE, callCustomer } from '../../services/appCall';
import { avatarColours, initials } from './avatar';
import '../Routes/StatusUpdateSheet.css';
import './FollowUpDetail.css';

/**
 * One customer on the Calls page, live from Zoho.
 *   Deal (Follow Up / Booked / Dismissed): Quote · Report photos · Notes; Book job / Dismiss up top;
 *     text, schedule a call, no answer and Call along the bottom.
 *     Book job → pick the package → check the invoice lines → one tap "Confirm booking & send invoice"
 *     (Books invoice from that quote). A booked deal whose invoice hasn't gone out keeps a Send invoice button.
 *   Inspection (Needs Quote / No Show): details + Notes; Open inspection (build the quote) or Rebook.
 * Every action is written to Zoho (Deal stage, Tasks, Notes, tags) and the board reloads.
 */

const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const wholeMoney = (n) => `$${Math.round(n || 0).toLocaleString('en-CA')}`;
const prettyPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
};
const when = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const day = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '');

// what the invoice will say (Books adds 13% HST after the discount — same maths as the quote) and where it goes
const InvoiceReview = ({ quote, item }) => {
  const sub = (quote.lines || []).reduce((s, l) => s + l.qty * l.rate, 0);
  const disc = quote.discount ? quote.discount.value : 0;
  const hst = Math.round((sub - disc) * 0.13 * 100) / 100;
  return (
    <div className="inv-review">
      <div className="inv-review-name">{quote.name} <span>{quote.number}</span></div>
      <ul className="inv-lines">
        {(quote.lines || []).map((l, i) => (
          <li key={i}>
            <span className="inv-line-name">{l.name}{l.qty !== 1 ? <em> {l.qty.toLocaleString('en-CA')} × {money(l.rate)}</em> : null}</span>
            <b>{money(l.qty * l.rate)}</b>
          </li>
        ))}
      </ul>
      <div className="inv-totals">
        <div><span>Subtotal</span><b>{money(sub)}</b></div>
        {disc > 0 && <div><span>{quote.discount.name || 'Discount'}</span><b>−{money(disc)}</b></div>}
        <div><span>HST 13%</span><b>{money(hst)}</b></div>
        <div className="inv-total"><span>Total</span><b>{money(zohoQuoteTotal(quote))}</b></div>
      </div>
      <p className="status-help">
        {/* email only; a text only when there's no email (Heemo 2026-11-01) */}
        {item.email ? (
          <>Emailed to <b>{item.email}</b>.</>
        ) : item.phone ? (
          <><b>No email on file</b> — the pay link will be texted to <b>{prettyPhone(item.phone)}</b> instead.</>
        ) : (
          <><b>No email or mobile on file</b> — add one in Zoho first.</>
        )}{' '}
        They can pay in full or a deposit by card; e-transfer and cash are recorded on the Job.
      </p>
    </div>
  );
};

const STAGE_TEXT = {
  needsQuote: () => 'Needs quote',
  noShow: (i) => (i.zohoStage === 'Cancelled' ? 'Cancelled' : 'Rebook'),
  followUp: (i) => (i.nextCall ? `Call ${day(i.nextCall.dueDate)}` : 'Call now'),
  booked: () => 'Job booked',
  dismissed: (i) => `Lost${i.lostReason ? ` · ${i.lostReason}` : ''}`,
};

const CallDetail = ({ id, onBack, onOpenCommunication, onOpenInspection, onOpenRebook }) => {
  useCalls(); // re-render when the board reloads
  const item = getCallItem(id);
  const detail = useCallDetail(id);
  const sent = useSentInspections();
  const [tab, setTab] = useState(item && item.kind === 'deal' ? 'quote' : 'notes');
  const [quoteIdx, setQuoteIdx] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [sheet, setSheet] = useState(null); // 'book' | 'review' | 'dismiss' | 'schedule'
  const [bookIdx, setBookIdx] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [callOn, setCallOn] = useState('');
  const [preset, setPreset] = useState(null);
  const [sheetNote, setSheetNote] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (item) loadDetail(item);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- load once per customer
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // inspection photos come from the report the agent sent (shared by every account)
  const photos = useMemo(() => {
    if (!item || !item.inspectionId) return [];
    const entry = sent.find((s) => String(s.leadId) === String(item.inspectionId));
    return entry && entry.source ? collectPhotos(entry.source.frontPhoto, entry.source.atticData) : [];
  }, [sent, item]);

  if (!item) {
    return (
      <div className="fud">
        <div className="fud-bar">
          <button type="button" className="fud-back" onClick={onBack} aria-label="Back">‹</button>
          <span className="fud-bar-title">Customer</span>
        </div>
        <p className="fud-empty">This customer isn’t in the list any more.</p>
      </div>
    );
  }

  const isDeal = item.kind === 'deal';
  const quotes = (detail && detail.quotes) || [];
  const notes = (detail && detail.notes) || [];
  const top = quotes.reduce((best, q, i) => (zohoQuoteTotal(q) > zohoQuoteTotal(quotes[best]) ? i : best), 0);
  const selected = quotes[quoteIdx == null ? top : quoteIdx];
  const sections = [...new Set(photos.map((p) => p.section))];
  const [bg, fg] = avatarColours(item.name);
  const tel = `tel:${String(item.phone || '').replace(/[^\d+]/g, '')}`;
  const today = localDate(new Date());

  const run = async (fn, done) => {
    setBusy(true);
    try {
      const out = await fn();
      setToast(typeof out === 'string' && out ? out : done);
      return true;
    } catch (err) {
      setToast(`⚠ ${err.message}`);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const closeSheet = () => {
    setSheet(null);
    setLostReason('');
    setCallOn('');
    setPreset(null);
    setSheetNote('');
  };

  return (
    <div className="fud">
      <div className="fud-bar">
        <button type="button" className="fud-back" onClick={onBack} aria-label="Back">‹</button>
        <span className="fud-bar-title">{isDeal ? 'Follow-up' : { needsQuote: 'Needs quote', noShow: 'No show', dismissed: 'Dismissed' }[item.section]}</span>
        <button
          type="button"
          className="fud-icon-btn"
          aria-label={item.priority ? 'Remove priority' : 'Mark priority'}
          onClick={() => run(() => togglePriority(item), item.priority ? 'Priority removed' : '⭐ Marked priority')}
        >
          {item.priority ? '★' : '☆'}
        </button>
        {isDeal && (
          <button
            type="button"
            className="fud-book"
              disabled={item.section === 'booked' || busy}
              onClick={() => {
                setBookIdx(quotes.length ? top : null);
                setSheet('book');
              }}
            >
            ✓ {item.section === 'booked' ? 'Booked' : 'Book job'}
          </button>
        )}
        {isDeal && INVOICE_LIVE && item.section === 'booked' && item.invoiceNotSent && (
          <button
            type="button"
            className="fud-book"
            disabled={busy || quotes.length === 0}
            onClick={() => {
              setBookIdx(invoiceQuoteIdx(quotes));
              setSheet('review');
            }}
          >
            🧾 Send invoice
          </button>
        )}
        {/* any customer can say they're not interested any more — Needs Quote and No Show too */}
        <button type="button" className="fud-dismiss" disabled={item.section === 'dismissed' || busy} onClick={() => setSheet('dismiss')}>
          ⊗ {item.section === 'dismissed' ? 'Dismissed' : 'Dismiss'}
        </button>
      </div>

      <div className="fud-scroll">
        <div className="fud-profile">
          <span className="fud-avatar" style={{ background: bg, color: fg }}>{initials(item.name)}</span>
          <div className="fud-who">
            <h2>{item.name}</h2>
            <p className="fud-address">{item.address}</p>
            {item.phone && <a className="fud-phone" href={tel}>📞 {prettyPhone(item.phone)}</a>}
            <div className="fud-meta">
              {item.homeType}
              <span className={`fud-stage ${item.section === 'followUp' ? 'call_now' : item.section === 'booked' ? 'booked' : item.section === 'dismissed' ? 'dismissed' : 'scheduled'}`}>
                {STAGE_TEXT[item.section](item)}
              </span>
            </div>
            {item.concern && <p className="fud-address">Concern: {item.concern}</p>}
            {!isDeal && item.appointmentStart && <p className="fud-address">Appointment: {when(item.appointmentStart)}{item.agent ? ` · ${item.agent}` : ''}</p>}
            {item.officeTasks && item.officeTasks.map((t) => (
              <p key={t.id} className="fud-address">📋 Office task due {day(t.dueDate)}: {t.description.split('\n')[0]}</p>
            ))}
          </div>
        </div>

        {!isDeal && (
          <div className="fud-panel">
            {item.section === 'needsQuote' && onOpenInspection && (
              <button type="button" className="fud-call" style={{ width: '100%' }} onClick={() => onOpenInspection(item)}>
                📝 Open inspection & build the quote
              </button>
            )}
            {(item.section === 'noShow' || item.section === 'dismissed') && onOpenRebook && (
              <button type="button" className="fud-call" style={{ width: '100%' }} onClick={() => onOpenRebook(item)}>
                ↺ Rebook the visit
              </button>
            )}
          </div>
        )}

        <div className="fud-tabs">
          {(isDeal
            ? [['quote', `Quote${quotes.length > 1 ? ` (${quotes.length})` : ''}`], ['report', 'Report'], ['notes', `Notes${notes.length ? ` (${notes.length})` : ''}`]]
            : [['notes', `Notes${notes.length ? ` (${notes.length})` : ''}`]]
          ).map(([key, label]) => (
            <button key={key} type="button" className={`fud-tab${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>

        {detail && detail.loading && <p className="fud-empty">Loading from Zoho…</p>}
        {detail && detail.error && <p className="fud-empty">⚠ {detail.error}</p>}

        {tab === 'quote' && isDeal && (
          <div className="fud-panel">
            {quotes.length === 0 && !(detail && detail.loading) && <p className="fud-empty">No quotes found in Zoho.</p>}
            {selected && (
              <>
                <div className="fud-quote-chips">
                  {quotes.map((q, i) => (
                    <button key={`${q.number}-${i}`} type="button" className={`fud-quote-chip${q === selected ? ' active' : ''}`} onClick={() => setQuoteIdx(i)}>
                      {q.number} <b>{wholeMoney(zohoQuoteTotal(q))}</b>
                    </button>
                  ))}
                </div>
                <div className="fud-section-label">
                  Breakdown <span>· {selected.name}</span>
                </div>
                <div className="fud-breakdown">
                  <div className="fud-category">
                    {selected.lines.map((l, i) => (
                      <div key={`${l.name}-${i}`} className="fud-line">
                        <div className="fud-line-top">
                          <b>{l.name}</b>
                          <span>{money(l.qty * l.rate)}</span>
                        </div>
                        {l.description && <p>{l.description}</p>}
                        <small>{l.qty.toLocaleString('en-CA')} × {money(l.rate)}</small>
                      </div>
                    ))}
                  </div>
                  <div className="fud-totals">
                    {selected.discount && (
                      <div className="fud-discount"><span>{selected.discount.name}</span><span>−{money(selected.discount.value)}</span></div>
                    )}
                    <div className="fud-grand"><span>Total (incl. HST)</span><span>{money(zohoQuoteTotal(selected))}</span></div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'report' && isDeal && (
          <div className="fud-panel">
            {sections.length === 0 && <p className="fud-empty">No inspection photos on this phone’s shared reports.</p>}
            {sections.map((section) => {
              const inSection = photos.filter((p) => p.section === section);
              return (
                <div key={section} className="fud-photo-group">
                  <div className="fud-section-label">{section} <span>· {inSection.length}</span></div>
                  <div className="fud-photo-grid">
                    {inSection.map((p) => (
                      <button key={p.src} type="button" className="fud-photo" onClick={() => setViewer(photos.indexOf(p))}>
                        <img src={p.src} alt={section} />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'notes' && (
          <div className="fud-panel">
            <div className="fud-add-note">
              <textarea rows={2} placeholder="Add a note about this customer…" value={draftNote} onChange={(e) => setDraftNote(e.target.value)} />
              <button
                type="button"
                disabled={!draftNote.trim() || busy}
                onClick={async () => {
                  if (await run(() => addCallNote(item, draftNote), 'Note saved to Zoho')) setDraftNote('');
                }}
              >
                Add note
              </button>
            </div>
            {notes.length === 0 && !(detail && detail.loading) && <p className="fud-empty">No notes yet.</p>}
            {[...notes].reverse().map((n) => (
              <div key={n.id} className="fud-note">
                <div className="fud-note-head">
                  <b>{n.author}</b>
                  <span>{when(n.at)}</span>
                </div>
                {n.title && !/^App: Note$/.test(n.title) && <small>{n.title.replace(/^App: /, '')}</small>}
                <p>{n.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fud-actions">
        {onOpenCommunication && item.phone ? (
          <button type="button" className="fud-icon-btn" aria-label="Text" onClick={() => onOpenCommunication(item.inspectionId || item.id, item.id)}>💬</button>
        ) : (
          <a className="fud-icon-btn" href={`sms:${String(item.phone || '').replace(/[^\d+]/g, '')}`} aria-label="Text">💬</a>
        )}
        {isDeal && (
          <>
            <button type="button" className="fud-icon-btn" aria-label="Schedule follow-up" disabled={busy} onClick={() => setSheet('schedule')}>📅</button>
            <button
              type="button"
              className="fud-icon-btn no-answer"
              aria-label="Called — no answer"
              disabled={busy}
              onClick={() => run(() => noAnswerCall(item), 'No answer — next call set for the next working day')}
            >
              📵
            </button>
          </>
        )}
        {/* from the app the customer sees (249) 700-4555 — the app itself is the phone */}
        {APP_CALLS_LIVE && item.phone ? (
          <button type="button" className="fud-call" disabled={busy} onClick={() => run(() => callCustomer(item.phone, item.name), 'Calling from (249) 700-4555…')}>
            📞 Call now
          </button>
        ) : (
          <a className="fud-call" href={tel}>📞 Call now</a>
        )}
      </div>

      {toast && <div className="fud-toast">{toast}</div>}

      {viewer != null && photos[viewer] && (
        <div className="fud-viewer" onClick={(e) => e.target === e.currentTarget && setViewer(null)}>
          <button type="button" className="fud-viewer-close" onClick={() => setViewer(null)} aria-label="Close">✕</button>
          <img src={photos[viewer].src} alt={photos[viewer].section} />
          <div className="fud-viewer-bar">
            <button type="button" onClick={() => setViewer((viewer - 1 + photos.length) % photos.length)} aria-label="Previous">‹</button>
            <span>{photos[viewer].section} · {viewer + 1} / {photos.length}</span>
            <button type="button" onClick={() => setViewer((viewer + 1) % photos.length)} aria-label="Next">›</button>
          </div>
        </div>
      )}

      {sheet && (
        <div className="status-sheet-overlay" onClick={(e) => e.target === e.currentTarget && closeSheet()}>
          <div className="status-sheet" role="dialog">
            <div className="status-sheet-grab" />
            <div className="status-sheet-head">
              <div>
                <h3>
                  {{ book: 'Which package did they book?', review: 'Check the invoice', dismiss: 'Why are they not going ahead?' }[sheet] || 'Next follow-up call'}
                </h3>
                <p>{item.name}</p>
              </div>
              <button type="button" className="status-sheet-close" onClick={closeSheet} aria-label="Close">✕</button>
            </div>
            <div className="status-sheet-body">
              {sheet === 'review' && quotes[bookIdx] && <InvoiceReview quote={quotes[bookIdx]} item={item} />}
              {sheet === 'book' && (
                <div className="fud-book-options" role="radiogroup">
                  {quotes.length === 0 && <p className="status-help">No quotes loaded from Zoho yet.</p>}
                  {quotes.map((q, i) => (
                    <button
                      key={`${q.number}-${i}`}
                      type="button"
                      role="radio"
                      aria-checked={bookIdx === i}
                      className={`fud-book-option${bookIdx === i ? ' selected' : ''}`}
                      onClick={() => setBookIdx(i)}
                    >
                      <span className="fud-book-radio" aria-hidden="true" />
                      <span className="fud-book-name">{q.name}</span>
                      <span className="fud-book-total">{money(zohoQuoteTotal(q))}</span>
                    </button>
                  ))}
                </div>
              )}
              {sheet === 'dismiss' && (
                <div className="fud-book-options" role="radiogroup">
                  {LOST_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      role="radio"
                      aria-checked={lostReason === r}
                      className={`fud-book-option${lostReason === r ? ' selected' : ''}`}
                      onClick={() => setLostReason(r)}
                    >
                      <span className="fud-book-radio" aria-hidden="true" />
                      <span className="fud-book-name">{r}</span>
                    </button>
                  ))}
                </div>
              )}
              {sheet === 'schedule' && (
                <div className="status-reschedule">
                  <div className="fud-quote-chips">
                    {FOLLOW_UP_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`fud-quote-chip${preset === p.id ? ' active' : ''}`}
                        onClick={() => {
                          setPreset(p.id);
                          setCallOn(p.date());
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                    <button type="button" className={`fud-quote-chip${preset === 'date' ? ' active' : ''}`} onClick={() => { setPreset('date'); setCallOn(''); }}>
                      📅 Pick a date
                    </button>
                  </div>
                  {preset === 'date' && (
                    <input type="date" className="status-date" min={today} value={callOn} onChange={(e) => setCallOn(e.target.value)} />
                  )}
                  {callOn && <p className="status-help">Shows under Follow Up → Due on {day(callOn)}.</p>}
                </div>
              )}
              {sheet !== 'review' && (
                <div className="status-note">
                  <label className="status-label" htmlFor="cd-note">Note <span className="status-optional">optional</span></label>
                  <textarea id="cd-note" className="status-textarea" rows={3} value={sheetNote} onChange={(e) => setSheetNote(e.target.value)} />
                </div>
              )}
            </div>
            <div className="status-sheet-foot">
              {sheet === 'review' && item.section !== 'booked' ? (
                <button type="button" className="status-cancel" onClick={() => setSheet('book')} disabled={busy}>‹ Back</button>
              ) : (
                <button type="button" className="status-cancel" onClick={closeSheet}>Cancel</button>
              )}
              {sheet === 'book' && (
                <button
                  type="button"
                  className="status-save"
                  disabled={busy || bookIdx == null || !quotes[bookIdx]}
                  onClick={async () => {
                    // live: review the invoice lines first; test mode books straight away
                    if (INVOICE_LIVE && quotes[bookIdx].zohoId) setSheet('review');
                    else if (await run(() => bookCall(item, quotes[bookIdx], sheetNote, quotes), 'Job booked')) closeSheet();
                  }}
                >
                  {quotes[bookIdx] ? (INVOICE_LIVE ? 'Review invoice ›' : `Book ${money(zohoQuoteTotal(quotes[bookIdx]))}`) : 'Pick a package'}
                </button>
              )}
              {sheet === 'review' && (
                <button
                  type="button"
                  className="status-save"
                  disabled={busy || !quotes[bookIdx] || (!item.email && !item.phone)}
                  onClick={async () => {
                    const q = quotes[bookIdx];
                    let booked = item.section === 'booked';
                    const done = await run(async () => {
                      // one tap: book (unless already booked) then send the invoice from the same quote
                      if (!booked) {
                        await bookCall(item, q, sheetNote, quotes);
                        booked = true;
                      }
                      try {
                        const out = await sendInvoice(item, q);
                        return `🧾 Booked — ${out.invoiceNumber} sent, ${money(out.total)}${out.noEmail ? ' (texted only)' : out.emailed ? '' : ' (email failed — send it from Books)'}`;
                      } catch (err) {
                        throw new Error(`${item.section !== 'booked' ? 'Job booked, but the' : 'The'} invoice didn’t go out: ${err.message}. Use 🧾 Send invoice to try again.`);
                      }
                    }, 'Booked');
                    // a failed booking keeps the sheet open; a booked job whose invoice failed keeps its Send invoice button
                    if (done || (booked && item.section !== 'booked')) closeSheet();
                  }}
                >
                  {busy ? 'Booking & sending…' : item.section === 'booked' ? `Send invoice ${money(zohoQuoteTotal(quotes[bookIdx]))}` : 'Confirm booking & send invoice'}
                </button>
              )}
              {sheet === 'dismiss' && (
                <button
                  type="button"
                  className="status-save cancelled"
                  disabled={busy || !lostReason}
                  onClick={async () => {
                    if (await run(() => dismissCall(item, lostReason, sheetNote), 'Dismissed')) closeSheet();
                  }}
                >
                  {lostReason ? 'Dismiss' : 'Pick a reason'}
                </button>
              )}
              {sheet === 'schedule' && (
                <button
                  type="button"
                  className="status-save"
                  disabled={busy || !callOn}
                  onClick={async () => {
                    if (await run(() => scheduleCall(item, callOn, sheetNote), `Call set for ${day(callOn)}`)) closeSheet();
                  }}
                >
                  {callOn ? 'Save call date' : 'Pick a date'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallDetail;
